'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';

const VEHICLE_CLASSES = ['Business Saloon', 'Luxury Saloon', 'Luxury SUV', 'Executive Van'];

const VEHICLE_FLEET = [
  { name: 'Business Saloon', desc: 'Mercedes E-Class or equivalent. Ideal for airport transfers and city journeys.', capacity: '3 passengers', price: 'From $80', icon: '🚗' },
  { name: 'Luxury Saloon', desc: 'Mercedes S-Class, BMW 7 Series or equivalent. The pinnacle of chauffeur comfort.', capacity: '3 passengers', price: 'From $140', icon: '🚘' },
  { name: 'Luxury SUV', desc: 'Range Rover, Mercedes GLS or equivalent. Perfect for families and extra luggage.', capacity: '6 passengers', price: 'From $180', icon: '🚙' },
  { name: 'Executive Van', desc: 'Mercedes V-Class or equivalent. Ideal for groups and corporate transfers.', capacity: '7 passengers', price: 'From $220', icon: '🚐' },
];

const POPULAR_ROUTES = [
  { from: 'Heathrow Airport (LHR)', to: 'Central London', vehicle: 'Luxury Saloon', price: 'From $140' },
  { from: 'Dubai International (DXB)', to: 'Burj Al Arab', vehicle: 'Luxury SUV', price: 'From $180' },
  { from: 'Charles de Gaulle (CDG)', to: 'Paris City Centre', vehicle: 'Business Saloon', price: 'From $120' },
  { from: 'JFK Airport', to: 'Manhattan', vehicle: 'Luxury Saloon', price: 'From $160' },
  { from: 'Singapore Changi (SIN)', to: 'Marina Bay Sands', vehicle: 'Business Saloon', price: 'From $90' },
  { from: 'Velana International (MLE)', to: 'Male City', vehicle: 'Luxury SUV', price: 'From $200' },
];

export default function TransfersPage() {
  const router = useRouter();
  const [pickup, setPickup] = useState('');
  const [dropoff, setDropoff] = useState('');
  const [pickupTime, setPickupTime] = useState('');
  const [passengers, setPassengers] = useState(2);
  const [vehicleClass, setVehicleClass] = useState('Luxury Saloon');

  const handleSearch = () => {
    const params = new URLSearchParams({
      vertical: 'transfer',
      destination: dropoff,
      checkIn: pickupTime,
      travelers: String(passengers),
      budget: vehicleClass,
    });
    router?.push(`/booking?${params?.toString()}`);
  };

  return (
    <div className="min-h-screen bg-[var(--background)] text-[var(--foreground)]">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[var(--surface-2)]/95 backdrop-blur-xl border-b border-[var(--border)] h-16 flex items-center px-6">
        <Link href="/" className="text-[var(--primary)] font-bold tracking-widest uppercase mr-8" style={{ fontFamily: 'var(--font-serif)' }}>WorldwayLuxe</Link>
        <div className="flex items-center gap-6 text-sm text-[var(--muted-foreground)]">
          <Link href="/flights" className="hover:text-[var(--primary)] transition-colors">Flights</Link>
          <Link href="/hotels" className="hover:text-[var(--primary)] transition-colors">Hotels</Link>
          <Link href="/private-jets" className="hover:text-[var(--primary)] transition-colors">Private Jets</Link>
          <Link href="/activities" className="hover:text-[var(--primary)] transition-colors">Activities</Link>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <Link href="/auth/login" className="text-sm text-[var(--muted-foreground)] hover:text-[var(--foreground)] px-4 py-2">Sign In</Link>
          <Link href="/auth/register" className="btn-primary text-sm px-5 py-2 rounded-lg">Join Now</Link>
        </div>
      </nav>
      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#050810] via-[var(--background)] to-[#0D1525]" />
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(ellipse 80% 60% at 50% 20%, rgba(201,168,76,0.15) 0%, transparent 70%)' }} />
        <div className="relative z-10 max-w-5xl mx-auto px-6">
          <p className="label-caps mb-3 text-[var(--primary)]">Transfers</p>
          <h1 className="text-4xl md:text-6xl font-bold mb-4" style={{ fontFamily: 'var(--font-serif)' }}>
            Kerb to Cabin,<br />
            <span className="gold-text-gradient">Seamlessly.</span>
          </h1>
          <p className="text-lg text-[var(--muted-foreground)] mb-10 max-w-xl">
            Chauffeured saloons, SUVs, and armored fleets on request. Available in 150+ cities worldwide.
          </p>

          {/* Search Widget */}
          <div className="glass-card rounded-2xl p-6 border border-[rgba(201,168,76,0.15)]">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[var(--muted-foreground)] mb-1">Pickup Location</label>
                <input type="text" placeholder="Airport, hotel or address" value={pickup} onChange={(e) => setPickup(e?.target?.value)} className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm text-[var(--foreground)] placeholder-[var(--muted-foreground)] focus:outline-none focus:border-[var(--primary)]" />
              </div>
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[var(--muted-foreground)] mb-1">Dropoff Location</label>
                <input type="text" placeholder="Airport, hotel or address" value={dropoff} onChange={(e) => setDropoff(e?.target?.value)} className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm text-[var(--foreground)] placeholder-[var(--muted-foreground)] focus:outline-none focus:border-[var(--primary)]" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[var(--muted-foreground)] mb-1">Pickup Time</label>
                <input type="datetime-local" value={pickupTime} onChange={(e) => setPickupTime(e?.target?.value)} className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm text-[var(--foreground)] focus:outline-none focus:border-[var(--primary)]" />
              </div>
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[var(--muted-foreground)] mb-1">Passengers</label>
                <select value={passengers} onChange={(e) => setPassengers(Number(e?.target?.value))} className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm text-[var(--foreground)] focus:outline-none focus:border-[var(--primary)]">
                  {[1, 2, 3, 4, 5, 6, 7]?.map((n) => <option key={n} value={n}>{n} {n === 1 ? 'Passenger' : 'Passengers'}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[var(--muted-foreground)] mb-1">Vehicle Class</label>
                <select value={vehicleClass} onChange={(e) => setVehicleClass(e?.target?.value)} className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm text-[var(--foreground)] focus:outline-none focus:border-[var(--primary)]">
                  {VEHICLE_CLASSES?.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <button onClick={handleSearch} className="btn-primary w-full py-3 rounded-xl text-sm font-semibold">Search Transfers</button>
          </div>
        </div>
      </section>
      {/* Fleet */}
      <section className="py-16 border-t border-[var(--border)]">
        <div className="max-w-5xl mx-auto px-6">
          <p className="label-caps mb-2">Our Fleet</p>
          <h2 className="text-2xl font-bold mb-8" style={{ fontFamily: 'var(--font-serif)' }}>Luxury <span className="gold-text-gradient">Vehicle Classes</span></h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {VEHICLE_FLEET?.map((v) => (
              <div key={v?.name} className="glass-card rounded-xl p-5 hover:border-[rgba(201,168,76,0.3)] transition-all cursor-pointer" onClick={() => setVehicleClass(v?.name)}>
                <div className="text-3xl mb-3">{v?.icon}</div>
                <h3 className="text-sm font-bold mb-2" style={{ fontFamily: 'var(--font-serif)' }}>{v?.name}</h3>
                <p className="text-xs text-[var(--muted-foreground)] mb-2 leading-relaxed">{v?.desc}</p>
                <p className="text-xs text-[var(--muted-foreground)] mb-2">Up to {v?.capacity}</p>
                <p className="text-xs font-semibold text-[var(--primary)]">{v?.price}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Popular Routes */}
      <section className="py-16 border-t border-[var(--border)]">
        <div className="max-w-5xl mx-auto px-6">
          <p className="label-caps mb-2">Popular Routes</p>
          <h2 className="text-2xl font-bold mb-8" style={{ fontFamily: 'var(--font-serif)' }}>Frequently Booked <span className="gold-text-gradient">Airport Transfers</span></h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {POPULAR_ROUTES?.map((r) => (
              <button key={`${r?.from}-${r?.to}`} onClick={() => { setPickup(r?.from); setDropoff(r?.to); setVehicleClass(r?.vehicle); }} className="glass-card rounded-xl p-4 text-left hover:border-[rgba(201,168,76,0.3)] transition-all group">
                <div className="text-xs text-[var(--muted-foreground)] mb-1">{r?.from}</div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[var(--primary)]">↓</span>
                  <span className="text-sm font-medium text-[var(--foreground)]">{r?.to}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-[var(--muted-foreground)]">{r?.vehicle}</span>
                  <span className="text-xs font-semibold text-[var(--primary)]">{r?.price}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>
      <footer className="border-t border-[var(--border)] py-8">
        <div className="max-w-5xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-xs text-[var(--muted-foreground)]">© 2026 Worldway Travels Group · All rights reserved.</div>
          <div className="flex items-center gap-6 text-xs text-[var(--muted-foreground)]">
            <Link href="/" className="hover:text-[var(--primary)] transition-colors">Home</Link>
            <Link href="/private-jets" className="hover:text-[var(--primary)] transition-colors">Private Jets</Link>
            <Link href="/flights" className="hover:text-[var(--primary)] transition-colors">Flights</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
